import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, visitorLogAPI, feedbackAPI, staffFeedbackAPI } from '../api';
import { FaHome, FaUsers, FaStar, FaChartLine, FaQrcode, FaDownload, FaTrash, FaPlus, FaEye, FaUserTie, FaExclamationTriangle, FaSearch, FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ITEMS_PER_PAGE = 20;

const AdminDashboard = () => {
  const [homes, setHomes] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [staffFeedbackStats, setStaffFeedbackStats] = useState(null);
  const [allLogs, setAllLogs] = useState([]);
  const [allFeedbacks, setAllFeedbacks] = useState([]);
  const [allStaffFeedbacks, setAllStaffFeedbacks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedHome, setSelectedHome] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [selectedStaffFeedback, setSelectedStaffFeedback] = useState(null);
  const [newHome, setNewHome] = useState({ title: '', email: '', password: '' });
  const [editingHome, setEditingHome] = useState(null);
  const [editHomeData, setEditHomeData] = useState({ email: '', password: '' });
  const [resolvingLog, setResolvingLog] = useState(null);
  const [resolveData, setResolveData] = useState({ status: '', addressFeedback: '' });
  const [resolvingFeedback, setResolvingFeedback] = useState(null);
  const [resolveFeedbackData, setResolveFeedbackData] = useState({ status: '', adminNotes: '' });
  const [resolvingStaffFeedback, setResolvingStaffFeedback] = useState(null);
  const [resolveStaffData, setResolveStaffData] = useState({ status: '', adminNotes: '' });

  // Filter states
  const [homesFilter, setHomesFilter] = useState({ search: '' });
  const [staffFilter, setStaffFilter] = useState({ search: '', type: '', severity: '', status: '', department: '', home: '' });
  const [visitorFilter, setVisitorFilter] = useState({ search: '', resolved: '', rating: '', home: '' });
  const [feedbackFilter, setFeedbackFilter] = useState({ search: '', type: '', serviceArea: '', home: '' });

  // Pagination states
  const [homesVisible, setHomesVisible] = useState(ITEMS_PER_PAGE);
  const [staffVisible, setStaffVisible] = useState(ITEMS_PER_PAGE);
  const [visitorVisible, setVisitorVisible] = useState(ITEMS_PER_PAGE);
  const [feedbackVisible, setFeedbackVisible] = useState(ITEMS_PER_PAGE);

  const homesScrollRef = useRef(null);
  const staffScrollRef = useRef(null);
  const visitorScrollRef = useRef(null);
  const feedbackScrollRef = useRef(null);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback((ref, visibleCount, setVisible, totalCount) => {
    if (!ref.current) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    if (scrollTop + clientHeight >= scrollHeight - 50 && visibleCount < totalCount) {
      setVisible(prev => Math.min(prev + ITEMS_PER_PAGE, totalCount));
    }
  }, []);

  useEffect(() => {
    const homesEl = homesScrollRef.current;
    const staffEl = staffScrollRef.current;
    const visitorEl = visitorScrollRef.current;
    const feedbackEl = feedbackScrollRef.current;

    const onHomesScroll = () => handleScroll(homesScrollRef, homesVisible, setHomesVisible, filteredHomes.length);
    const onStaffScroll = () => handleScroll(staffScrollRef, staffVisible, setStaffVisible, filteredStaffFeedbacks.length);
    const onVisitorScroll = () => handleScroll(visitorScrollRef, visitorVisible, setVisitorVisible, filteredLogs.length);
    const onFeedbackScroll = () => handleScroll(feedbackScrollRef, feedbackVisible, setFeedbackVisible, filteredFeedbacks.length);

    homesEl?.addEventListener('scroll', onHomesScroll);
    staffEl?.addEventListener('scroll', onStaffScroll);
    visitorEl?.addEventListener('scroll', onVisitorScroll);
    feedbackEl?.addEventListener('scroll', onFeedbackScroll);

    return () => {
      homesEl?.removeEventListener('scroll', onHomesScroll);
      staffEl?.removeEventListener('scroll', onStaffScroll);
      visitorEl?.removeEventListener('scroll', onVisitorScroll);
      feedbackEl?.removeEventListener('scroll', onFeedbackScroll);
    };
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [homesRes, vStatsRes, fStatsRes, logsRes, feedbacksRes, staffFeedbacksRes, staffStatsRes] = await Promise.all([
        authAPI.getHomes(),
        visitorLogAPI.getAllStats(),
        feedbackAPI.getAllStats(),
        visitorLogAPI.getAllLogs(),
        feedbackAPI.getAllFeedbacks(),
        axios.get(`${API_URL}/staff-feedback/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/staff-feedback/stats/all`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setHomes(homesRes.data);
      setVisitorStats(vStatsRes.data);
      setFeedbackStats(fStatsRes.data);
      setAllLogs(logsRes.data);
      setAllFeedbacks(feedbacksRes.data);
      setAllStaffFeedbacks(staffFeedbacksRes.data);
      setStaffFeedbackStats(staffStatsRes.data);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load data');
    }
  };

  const handleCreateHome = async (e) => {
    e.preventDefault();
    try {
      await authAPI.register(newHome);
      toast.success('Home created successfully!');
      setShowModal(false);
      setNewHome({ title: '', email: '', password: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create home');
    }
  };

  const handleDeleteHome = async (id) => {
    if (window.confirm('Are you sure you want to delete this home?')) {
      try {
        await authAPI.deleteHome(id);
        toast.success('Home deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete home');
      }
    }
  };

  const handleEditHome = (home) => {
    setEditingHome(home);
    setEditHomeData({ email: home.email, password: '' });
  };

  const handleSaveEditHome = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (editHomeData.email && editHomeData.email !== editingHome.email) {
        updateData.email = editHomeData.email;
      }
      if (editHomeData.password) {
        updateData.password = editHomeData.password;
      }
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        return;
      }
      await authAPI.updateHome(editingHome._id, updateData);
      toast.success('Home updated successfully!');
      setEditingHome(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update home');
    }
  };

  const handleResolveLog = async () => {
    try {
      await visitorLogAPI.resolveLog(resolvingLog._id, resolveData);
      toast.success('Visitor log updated successfully!');
      setResolvingLog(null);
      setResolveData({ status: '', addressFeedback: '' });
      setSelectedLog(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update visitor log');
    }
  };

  const handleResolveFeedback = async () => {
    try {
      await feedbackAPI.updateFeedback(resolvingFeedback._id, resolveFeedbackData);
      toast.success('Resident feedback updated successfully!');
      setResolvingFeedback(null);
      setResolveFeedbackData({ status: '', adminNotes: '' });
      setSelectedFeedback(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update feedback');
    }
  };

  const handleResolveStaffFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/staff-feedback/${resolvingStaffFeedback._id}`, resolveStaffData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff feedback updated successfully!');
      setResolvingStaffFeedback(null);
      setResolveStaffData({ status: '', adminNotes: '' });
      setSelectedStaffFeedback(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update staff feedback');
    }
  };

  const handleDeleteLog = async (id) => {
    if (window.confirm('Are you sure you want to delete this visitor log? This action cannot be undone.')) {
      try {
        await visitorLogAPI.deleteLog(id);
        toast.success('Visitor log deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete visitor log');
      }
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (window.confirm('Are you sure you want to delete this resident feedback? This action cannot be undone.')) {
      try {
        await feedbackAPI.deleteFeedback(id);
        toast.success('Resident feedback deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete resident feedback');
      }
    }
  };

  const handleDeleteStaffFeedback = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff feedback? This action cannot be undone.')) {
      try {
        await staffFeedbackAPI.deleteStaffFeedback(id);
        toast.success('Staff feedback deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete staff feedback');
      }
    }
  };

  // Sort latest first + filter logic
  const sortByDateDesc = (a, b, dateField = 'submittedAt') => new Date(b[dateField]) - new Date(a[dateField]);

  const filteredHomes = homes
    .filter(h => {
      const search = homesFilter.search.toLowerCase();
      if (search && !h.title?.toLowerCase().includes(search) && !h.email?.toLowerCase().includes(search) && !h.homeId?.toLowerCase().includes(search)) return false;
      return true;
    });

  const filteredStaffFeedbacks = allStaffFeedbacks
    .slice()
    .sort((a, b) => sortByDateDesc(a, b))
    .filter(f => {
      const search = staffFilter.search.toLowerCase();
      if (search && !f.fullName?.toLowerCase().includes(search) && !f.department?.toLowerCase().includes(search) && !f.feedbackDetails?.toLowerCase().includes(search) && !f.homeTitle?.toLowerCase().includes(search)) return false;
      if (staffFilter.type && f.feedbackType !== staffFilter.type) return false;
      if (staffFilter.severity && f.severityLevel !== staffFilter.severity) return false;
      if (staffFilter.status && f.status !== staffFilter.status) return false;
      if (staffFilter.department && f.department !== staffFilter.department) return false;
      if (staffFilter.home && f.homeTitle !== staffFilter.home) return false;
      return true;
    });

  const filteredLogs = allLogs
    .slice()
    .sort((a, b) => sortByDateDesc(a, b, 'visitDate'))
    .filter(l => {
      const search = visitorFilter.search.toLowerCase();
      if (search && !l.fullName?.toLowerCase().includes(search) && !l.residentName?.toLowerCase().includes(search) && !l.phoneNumber?.includes(search) && !l.homeTitle?.toLowerCase().includes(search)) return false;
      if (visitorFilter.resolved && l.purposeResolved !== visitorFilter.resolved) return false;
      if (visitorFilter.rating && l.experienceRating !== Number(visitorFilter.rating)) return false;
      if (visitorFilter.home && l.homeTitle !== visitorFilter.home) return false;
      return true;
    });

  const filteredFeedbacks = allFeedbacks
    .slice()
    .sort((a, b) => sortByDateDesc(a, b))
    .filter(f => {
      const search = feedbackFilter.search.toLowerCase();
      if (search && !f.residentFullName?.toLowerCase().includes(search) && !f.feedbackDetails?.toLowerCase().includes(search) && !f.serviceArea?.toLowerCase().includes(search) && !f.homeTitle?.toLowerCase().includes(search)) return false;
      if (feedbackFilter.type && f.feedbackType !== feedbackFilter.type) return false;
      if (feedbackFilter.serviceArea && f.serviceArea !== feedbackFilter.serviceArea) return false;
      if (feedbackFilter.home && f.homeTitle !== feedbackFilter.home) return false;
      return true;
    });

  // Unique values for filter dropdowns
  const uniqueDepartments = ['Nursing', 'Care Support', 'Administration', 'Kitchen', 'Maintenance', 'Security', 'Manager', 'Other'];
  const uniqueServiceAreas = [...new Set(allFeedbacks.map(f => f.serviceArea).filter(Boolean))];
  const uniqueHomeTitles = [...new Set(homes.map(h => h.title).filter(Boolean))];

  // Reset pagination when filters change
  useEffect(() => { setHomesVisible(ITEMS_PER_PAGE); }, [homesFilter]);
  useEffect(() => { setStaffVisible(ITEMS_PER_PAGE); }, [staffFilter]);
  useEffect(() => { setVisitorVisible(ITEMS_PER_PAGE); }, [visitorFilter]);
  useEffect(() => { setFeedbackVisible(ITEMS_PER_PAGE); }, [feedbackFilter]);

  const downloadQRCode = (home) => {
    const link = document.createElement('a');
    link.href = home.qrCode;
    link.download = `${home.title.replace(/\s+/g, '-')}-QR-Code.png`;
    link.click();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Low': return '#10b981';
      case 'Medium': return '#f59e0b';
      case 'High': return '#ef4444';
      case 'Critical': return '#dc2626';
      default: return '#6b7280';
    }
  };

  return (
    <>
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaChartLine /> Admin Overview</h2>
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              <FaPlus /> Create New Home
            </button>
          </div>
          <div className="card-body">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><FaHome /></div>
                <div className="stat-content">
                  <div className="stat-label">Total Homes</div>
                  <div className="stat-value">{homes.length}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaUsers /></div>
                <div className="stat-content">
                  <div className="stat-label">Visitor Logs</div>
                  <div className="stat-value">{visitorStats?.totalLogs || 0}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaStar /></div>
                <div className="stat-content">
                  <div className="stat-label">Resident Feedbacks</div>
                  <div className="stat-value">{feedbackStats?.totalFeedbacks || 0}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaUserTie /></div>
                <div className="stat-content">
                  <div className="stat-label">Staff Feedbacks</div>
                  <div className="stat-value">{staffFeedbackStats?.totalFeedbacks || 0}</div>
                </div>
              </div>
              <div className="stat-card" style={{ background: staffFeedbackStats?.escalatedCount > 0 ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'linear-gradient(135deg, #1A2D8B 0%, #152469 100%)' }}>
                <div className="stat-icon"><FaExclamationTriangle /></div>
                <div className="stat-content">
                  <div className="stat-label">Escalated Issues</div>
                  <div className="stat-value">{staffFeedbackStats?.escalatedCount || 0}</div>
                </div>
              </div>
            </div>

            {visitorStats?.homeStats && visitorStats.homeStats.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: 'var(--primary)' }}>Home Performance</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={visitorStats.homeStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="title" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="logCount" fill="var(--primary)" name="Visitor Logs" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaHome /> Manage Homes</h2>
            <span className="badge badge-primary">{filteredHomes.length} of {homes.length}</span>
          </div>
          <div className="card-body">
            {homes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaHome /></div>
                <h3 className="empty-title">No Homes Yet</h3>
                <p className="empty-text">Create your first home to get started</p>
                <button onClick={() => setShowModal(true)} className="btn btn-primary">
                  <FaPlus /> Create Home
                </button>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search title, email, home ID..." className="filter-input" value={homesFilter.search} onChange={e => setHomesFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                </div>
                <div className="table-scroll-container" ref={homesScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Email</th>
                        <th>Home ID</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHomes.slice(0, homesVisible).map((home) => (
                        <tr key={home._id}>
                          <td><strong>{home.title}</strong></td>
                          <td>{home.email}</td>
                          <td><code style={{ fontSize: '0.75rem' }}>{home.homeId}</code></td>
                          <td>
                            <span className={`badge ${home.isActive ? 'badge-success' : 'badge-danger'}`}>
                              {home.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => handleEditHome(home)} className="btn btn-warning btn-sm" title="Edit Home">
                                <FaEdit />
                              </button>
                              <button onClick={() => setSelectedHome(home)} className="btn btn-secondary btn-sm">
                                <FaQrcode />
                              </button>
                              <button onClick={() => downloadQRCode(home)} className="btn btn-success btn-sm">
                                <FaDownload />
                              </button>
                              <button onClick={() => handleDeleteHome(home._id)} className="btn btn-danger btn-sm">
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredHomes.length === 0 && (
                    <div className="filter-empty">No homes match your search</div>
                  )}
                  {homesVisible < filteredHomes.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Staff Feedback List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaUserTie /> All Staff Feedback</h2>
            <span className="badge badge-primary">{filteredStaffFeedbacks.length} of {allStaffFeedbacks.length}</span>
          </div>
          <div className="card-body">
            {allStaffFeedbacks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaUserTie /></div>
                <h3 className="empty-title">No Staff Feedback Yet</h3>
                <p className="empty-text">Staff feedback will appear here once submitted</p>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search name, department, home..." className="filter-input" value={staffFilter.search} onChange={e => setStaffFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  <select className="filter-select" value={staffFilter.type} onChange={e => setStaffFilter(f => ({ ...f, type: e.target.value }))}>
                    <option value="">All Types</option>
                    <option value="Suggestion">Suggestion</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Appreciation">Appreciation</option>
                    <option value="Safety Concern">Safety Concern</option>
                    <option value="Other">Other</option>
                  </select>
                  <select className="filter-select" value={staffFilter.severity} onChange={e => setStaffFilter(f => ({ ...f, severity: e.target.value }))}>
                    <option value="">All Severity</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                  <select className="filter-select" value={staffFilter.status} onChange={e => setStaffFilter(f => ({ ...f, status: e.target.value }))}>
                    <option value="">All Status</option>
                    <option value="New">New</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Escalated">Escalated</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                  {uniqueDepartments.length > 0 && (
                    <select className="filter-select" value={staffFilter.department} onChange={e => setStaffFilter(f => ({ ...f, department: e.target.value }))}>
                      <option value="">All Departments</option>
                      {uniqueDepartments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  )}
                  {uniqueHomeTitles.length > 0 && (
                    <select className="filter-select" value={staffFilter.home} onChange={e => setStaffFilter(f => ({ ...f, home: e.target.value }))}>
                      <option value="">All Homes</option>
                      {uniqueHomeTitles.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  )}
                </div>
                <div className="table-scroll-container" ref={staffScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Staff Name</th>
                        <th>Department</th>
                        <th>Home</th>
                        <th>Type</th>
                        <th>Severity</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaffFeedbacks.slice(0, staffVisible).map((feedback) => (
                        <tr key={feedback._id} style={feedback.escalationFlag ? { background: '#fef2f2' } : {}}>
                          <td>{new Date(feedback.submittedAt).toLocaleDateString()}</td>
                          <td>
                            <strong>{feedback.fullName}</strong>
                            {feedback.isAnonymous && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Anonymous</span>}
                          </td>
                          <td>{feedback.department}</td>
                          <td><strong>{feedback.homeTitle}</strong></td>
                          <td>
                            <span className={`badge ${
                              feedback.feedbackType === 'Safety Concern' || feedback.feedbackType === 'Complaint' ? 'badge-danger' :
                              feedback.feedbackType === 'Appreciation' ? 'badge-success' :
                              'badge-primary'
                            }`}>
                              {feedback.feedbackType}
                            </span>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: getSeverityColor(feedback.severityLevel),
                                color: 'white'
                              }}
                            >
                              {feedback.severityLevel}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              feedback.status === 'New' ? 'badge-primary' :
                              feedback.status === 'Under Review' ? 'badge-warning' :
                              feedback.status === 'Escalated' ? 'badge-danger' :
                              feedback.status === 'Resolved' ? 'badge-success' :
                              'badge-secondary'
                            }`}>
                              {feedback.status}
                            </span>
                            {feedback.escalationFlag && (
                              <FaExclamationTriangle style={{ marginLeft: '0.5rem', color: '#dc2626' }} title="Escalated" />
                            )}
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setSelectedStaffFeedback(feedback)} className="btn btn-primary btn-sm">
                              <FaEye /> View
                            </button>
                            <button onClick={() => {
                              setResolvingStaffFeedback(feedback);
                              setResolveStaffData({ status: feedback.status || 'New', adminNotes: feedback.adminNotes || '' });
                            }} className="btn btn-success btn-sm" style={{ background: '#10b981', borderColor: '#10b981' }}>
                              Resolve
                            </button>
                            <button onClick={() => handleDeleteStaffFeedback(feedback._id)} className="btn btn-danger btn-sm">
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredStaffFeedbacks.length === 0 && (
                    <div className="filter-empty">No staff feedback matches your filters</div>
                  )}
                  {staffVisible < filteredStaffFeedbacks.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Visitor Logs List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaUsers /> All Visitor Logs</h2>
            <span className="badge badge-primary">{filteredLogs.length} of {allLogs.length}</span>
          </div>
          <div className="card-body">
            {allLogs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaUsers /></div>
                <h3 className="empty-title">No Visitor Logs Yet</h3>
                <p className="empty-text">Logs will appear here once submitted</p>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search visitor, resident, phone, home..." className="filter-input" value={visitorFilter.search} onChange={e => setVisitorFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  <select className="filter-select" value={visitorFilter.resolved} onChange={e => setVisitorFilter(f => ({ ...f, resolved: e.target.value }))}>
                    <option value="">All Resolved</option>
                    <option value="Yes">Yes</option>
                    <option value="Partially">Partially</option>
                    <option value="No">No</option>
                  </select>
                  <select className="filter-select" value={visitorFilter.rating} onChange={e => setVisitorFilter(f => ({ ...f, rating: e.target.value }))}>
                    <option value="">All Ratings</option>
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="2">2 Stars</option>
                    <option value="1">1 Star</option>
                  </select>
                  {uniqueHomeTitles.length > 0 && (
                    <select className="filter-select" value={visitorFilter.home} onChange={e => setVisitorFilter(f => ({ ...f, home: e.target.value }))}>
                      <option value="">All Homes</option>
                      {uniqueHomeTitles.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  )}
                </div>
                <div className="table-scroll-container" ref={visitorScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Visitor Name</th>
                        <th>Phone</th>
                        <th>Resident</th>
                        <th>Home</th>
                        <th>Type</th>
                        <th>Rating</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.slice(0, visitorVisible).map((log) => (
                        <tr key={log._id}>
                          <td>{new Date(log.visitDate).toLocaleDateString()}</td>
                          <td>
                            <strong>{log.fullName}</strong>
                            {log.isAnonymous && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Anonymous</span>}
                          </td>
                          <td>{log.phoneNumber}</td>
                          <td>{log.residentName}</td>
                          <td><strong>{log.homeTitle}</strong></td>
                          <td>
                            <span className="badge badge-primary">{log.visitType}</span>
                          </td>
                          <td><strong>{log.experienceRating}</strong> / 5</td>
                          <td>
                            <span className={`badge ${log.status === 'Resolved' || log.status === 'Closed' ? 'badge-success' : log.status === 'In Progress' ? 'badge-warning' : 'badge-secondary'}`}>
                              {log.status || 'Pending'}
                            </span>
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setSelectedLog(log)} className="btn btn-primary btn-sm">
                              <FaEye /> View
                            </button>
                            <button onClick={() => {
                              setResolvingLog(log);
                              setResolveData({ status: log.status || 'Pending', addressFeedback: log.addressFeedback || '' });
                            }} className="btn btn-success btn-sm" style={{ background: '#10b981', borderColor: '#10b981' }}>
                              Resolve
                            </button>
                            <button onClick={() => handleDeleteLog(log._id)} className="btn btn-danger btn-sm">
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredLogs.length === 0 && (
                    <div className="filter-empty">No visitor logs match your filters</div>
                  )}
                  {visitorVisible < filteredLogs.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Resident Feedback List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaStar /> All Resident Feedback</h2>
            <span className="badge badge-primary">{filteredFeedbacks.length} of {allFeedbacks.length}</span>
          </div>
          <div className="card-body">
            {allFeedbacks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaStar /></div>
                <h3 className="empty-title">No Feedback Yet</h3>
                <p className="empty-text">Feedback will appear here once submitted</p>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search resident, details, area, home..." className="filter-input" value={feedbackFilter.search} onChange={e => setFeedbackFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  <select className="filter-select" value={feedbackFilter.type} onChange={e => setFeedbackFilter(f => ({ ...f, type: e.target.value }))}>
                    <option value="">All Types</option>
                    <option value="Suggestion">Suggestion</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Appreciation">Appreciation</option>
                    <option value="Other">Other</option>
                  </select>
                  {uniqueServiceAreas.length > 0 && (
                    <select className="filter-select" value={feedbackFilter.serviceArea} onChange={e => setFeedbackFilter(f => ({ ...f, serviceArea: e.target.value }))}>
                      <option value="">All Service Areas</option>
                      {uniqueServiceAreas.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  )}
                  {uniqueHomeTitles.length > 0 && (
                    <select className="filter-select" value={feedbackFilter.home} onChange={e => setFeedbackFilter(f => ({ ...f, home: e.target.value }))}>
                      <option value="">All Homes</option>
                      {uniqueHomeTitles.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  )}
                </div>
                <div className="table-scroll-container" ref={feedbackScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Resident Name</th>
                        <th>Home</th>
                        <th>Type</th>
                        <th>Rating</th>
                        <th>Service Area</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFeedbacks.slice(0, feedbackVisible).map((feedback) => (
                        <tr key={feedback._id}>
                          <td>{new Date(feedback.submittedAt).toLocaleDateString()}</td>
                          <td>
                            <strong>{feedback.residentFullName}</strong>
                            {feedback.isAnonymous && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Anonymous</span>}
                          </td>
                          <td><strong>{feedback.homeTitle}</strong></td>
                          <td>
                            <span className={`badge ${feedback.feedbackType === 'Complaint' ? 'badge-danger' : feedback.feedbackType === 'Appreciation' ? 'badge-success' : 'badge-primary'}`}>
                              {feedback.feedbackType}
                            </span>
                          </td>
                          <td>{feedback.experienceRating}</td>
                          <td>{feedback.serviceArea}</td>
                          <td>
                            <span className={`badge ${feedback.status === 'Resolved' || feedback.status === 'Closed' ? 'badge-success' : feedback.status === 'In Progress' ? 'badge-warning' : feedback.status === 'Acknowledged' ? 'badge-primary' : 'badge-secondary'}`}>
                              {feedback.status || 'Pending'}
                            </span>
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setSelectedFeedback(feedback)} className="btn btn-primary btn-sm">
                              <FaEye /> View
                            </button>
                            <button onClick={() => {
                              setResolvingFeedback(feedback);
                              setResolveFeedbackData({ status: feedback.status || 'Pending', adminNotes: feedback.adminNotes || '' });
                            }} className="btn btn-success btn-sm" style={{ background: '#10b981', borderColor: '#10b981' }}>
                              Resolve
                            </button>
                            <button onClick={() => handleDeleteFeedback(feedback._id)} className="btn btn-danger btn-sm">
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredFeedbacks.length === 0 && (
                    <div className="filter-empty">No resident feedback matches your filters</div>
                  )}
                  {feedbackVisible < filteredFeedbacks.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MODALS */}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Home</h2>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateHome}>
                <div className="form-group">
                  <label className="form-label required">Home Title</label>
                  <input type="text" className="form-input" value={newHome.title} onChange={(e) => setNewHome({ ...newHome, title: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label required">Email</label>
                  <input type="email" className="form-input" value={newHome.email} onChange={(e) => setNewHome({ ...newHome, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label required">Password</label>
                  <input type="password" className="form-input" value={newHome.password} onChange={(e) => setNewHome({ ...newHome, password: e.target.value })} required minLength={6} />
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                  <button type="submit" className="btn btn-primary">Create Home</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedHome && (
        <div className="modal-overlay" onClick={() => setSelectedHome(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">QR Code - {selectedHome.title}</h2>
            </div>
            <div className="modal-body">
              <div className="qr-display">
                <div className="qr-code">
                  <img src={selectedHome.qrCode} alt="QR Code" />
                </div>
                <div className="qr-info">
                  <p><strong>Home ID:</strong> {selectedHome.homeId}</p>
                  <p><strong>Email:</strong> {selectedHome.email}</p>
                  <p style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
                    Scan this QR code to access visitor/resident forms
                  </p>
                </div>
                <button onClick={() => downloadQRCode(selectedHome)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                  <FaDownload /> Download QR Code
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setSelectedHome(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Home Modal */}
      {editingHome && (
        <div className="modal-overlay" onClick={() => setEditingHome(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Home - {editingHome.title}</h2>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSaveEditHome}>
                <div className="form-group">
                  <label className="form-label">Home ID</label>
                  <input type="text" className="form-input" value={editingHome.homeId} disabled style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                </div>
                <div className="form-group">
                  <label className="form-label required">Email</label>
                  <input type="email" className="form-input" value={editHomeData.email} onChange={(e) => setEditHomeData({ ...editHomeData, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type="password" className="form-input" value={editHomeData.password} onChange={(e) => setEditHomeData({ ...editHomeData, password: e.target.value })} minLength={6} placeholder="Leave blank to keep current password" />
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={() => setEditingHome(null)} className="btn btn-secondary">Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Staff Feedback Detail Modal - NEW */}
      {selectedStaffFeedback && (
        <div className="modal-overlay" onClick={() => setSelectedStaffFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Staff Feedback Details</h2>
              {selectedStaffFeedback.escalationFlag && (
                <span className="badge badge-danger" style={{ marginLeft: '1rem' }}>
                  <FaExclamationTriangle /> ESCALATED
                </span>
              )}
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {/* Staff Information */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
                    Staff Information
                    {selectedStaffFeedback.isAnonymous && (
                      <span className="badge badge-secondary" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>Anonymous Submission</span>
                    )}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Full Name:</strong><br />
                      {selectedStaffFeedback.fullName}
                    </div>
                    <div>
                      <strong>Employee ID:</strong><br />
                      {selectedStaffFeedback.employeeId}
                    </div>
                    <div>
                      <strong>Department:</strong><br />
                      {selectedStaffFeedback.department}
                    </div>
                    {selectedStaffFeedback.role && (
                      <div>
                        <strong>Role:</strong><br />
                        {selectedStaffFeedback.role}
                      </div>
                    )}
                    <div>
                      <strong>Employment Type:</strong><br />
                      {selectedStaffFeedback.employmentType}
                    </div>
                    <div>
                      <strong>Home:</strong><br />
                      <strong>{selectedStaffFeedback.homeTitle}</strong>
                    </div>
                  </div>
                </div>

                {/* Feedback Details */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Feedback Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Submitted:</strong><br />
                      {new Date(selectedStaffFeedback.submittedAt).toLocaleDateString()}
                    </div>
                    <div>
                      <strong>Type:</strong><br />
                      <span className={`badge ${
                        selectedStaffFeedback.feedbackType === 'Safety Concern' || selectedStaffFeedback.feedbackType === 'Complaint' ? 'badge-danger' : 
                        selectedStaffFeedback.feedbackType === 'Appreciation' ? 'badge-success' : 
                        'badge-primary'
                      }`}>
                        {selectedStaffFeedback.feedbackType}
                      </span>
                    </div>
                    <div>
                      <strong>Severity:</strong><br />
                      <span className="badge" style={{ background: getSeverityColor(selectedStaffFeedback.severityLevel), color: 'white' }}>
                        {selectedStaffFeedback.severityLevel}
                      </span>
                    </div>
                    {selectedStaffFeedback.relatedArea && (
                      <div>
                        <strong>Related Area:</strong><br />
                        {selectedStaffFeedback.relatedArea}
                      </div>
                    )}
                    <div>
                      <strong>Status:</strong><br />
                      <span className={`badge ${
                        selectedStaffFeedback.status === 'New' ? 'badge-primary' :
                        selectedStaffFeedback.status === 'Under Review' ? 'badge-warning' :
                        selectedStaffFeedback.status === 'Escalated' ? 'badge-danger' :
                        selectedStaffFeedback.status === 'Resolved' ? 'badge-success' :
                        'badge-secondary'
                      }`}>
                        {selectedStaffFeedback.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Incident Information */}
                {(selectedStaffFeedback.incidentDate || selectedStaffFeedback.incidentTime) && (
                  <div>
                    <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Incident Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {selectedStaffFeedback.incidentDate && (
                        <div>
                          <strong>Date:</strong><br />
                          {new Date(selectedStaffFeedback.incidentDate).toLocaleDateString()}
                        </div>
                      )}
                      {selectedStaffFeedback.incidentTime && (
                        <div>
                          <strong>Time:</strong><br />
                          {selectedStaffFeedback.incidentTime}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Detailed Feedback */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Detailed Description</h3>
                  <div style={{ padding: '1.5rem', background: 'var(--gray-50)', borderRadius: '0.5rem', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
                    {selectedStaffFeedback.feedbackDetails}
                  </div>
                </div>

                {/* Supporting Documents */}
                {selectedStaffFeedback.supportingDocuments && selectedStaffFeedback.supportingDocuments.length > 0 && (
                  <div>
                    <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Supporting Documents ({selectedStaffFeedback.supportingDocuments.length})</h3>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {selectedStaffFeedback.supportingDocuments.map((doc, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            padding: '1rem', 
                            background: 'var(--gray-50)', 
                            borderRadius: '0.5rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between' 
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <FaDownload style={{ color: 'var(--primary)' }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{doc.fileName}</div>
                              <div style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                                {(doc.fileSize / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-primary btn-sm"
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Digital Signature */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Digital Signature</h3>
                  <div style={{ border: '2px solid var(--gray-300)', borderRadius: '0.5rem', padding: '1rem', background: 'white' }}>
                    <img src={selectedStaffFeedback.digitalSignature} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>

                {/* Admin Notes */}
                {selectedStaffFeedback.adminNotes && (
                  <div>
                    <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Admin Notes</h3>
                    <div style={{ padding: '1rem', background: 'var(--secondary)', borderRadius: '0.5rem' }}>
                      {selectedStaffFeedback.adminNotes}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setResolvingStaffFeedback(selectedStaffFeedback);
                setResolveStaffData({ status: selectedStaffFeedback.status || 'New', adminNotes: selectedStaffFeedback.adminNotes || '' });
              }} className="btn btn-primary">
                Resolve / Update Status
              </button>
              <button onClick={() => setSelectedStaffFeedback(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Log Detail Modal */}
      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Visitor Log Details</h2>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
                    Visitor Information
                    {selectedLog.isAnonymous && (
                      <span className="badge badge-secondary" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>Anonymous</span>
                    )}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Full Name:</strong><br />
                      {selectedLog.fullName}
                    </div>
                    <div>
                      <strong>Phone:</strong><br />
                      {selectedLog.phoneNumber}
                    </div>
                    <div>
                      <strong>Email:</strong><br />
                      {selectedLog.email}
                    </div>
                    <div>
                      <strong>Relationship:</strong><br />
                      {selectedLog.relationshipToResident}
                      {selectedLog.relationshipOther && ` (${selectedLog.relationshipOther})`}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Visit Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Date:</strong><br />
                      {new Date(selectedLog.visitDate).toLocaleDateString()}
                    </div>
                    <div>
                      <strong>Time:</strong><br />
                      {selectedLog.visitTime}
                    </div>
                    <div>
                      <strong>Resident Name:</strong><br />
                      {selectedLog.residentName}
                    </div>
                    <div>
                      <strong>Reason for Visit:</strong><br />
                      {selectedLog.reasonForVisit}
                      {selectedLog.reasonOther && ` (${selectedLog.reasonOther})`}
                    </div>
                    <div>
                      <strong>Visit Type:</strong><br />
                      <span className="badge badge-primary">{selectedLog.visitType}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Feedback</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <strong>Experience Rating:</strong><br />
                      <span style={{ fontSize: '1.5rem', color: '#fbbf24' }}>
                        {'★'.repeat(selectedLog.experienceRating)}{'☆'.repeat(5 - selectedLog.experienceRating)}
                      </span>
                      <span style={{ marginLeft: '0.5rem' }}>{selectedLog.experienceRating} / 5</span>
                    </div>
                    <div>
                      <strong>Purpose Resolved:</strong><br />
                      <span className={`badge ${selectedLog.purposeResolved === 'Yes' ? 'badge-success' : selectedLog.purposeResolved === 'No' ? 'badge-danger' : 'badge-warning'}`}>
                        {selectedLog.purposeResolved}
                      </span>
                    </div>
                  </div>
                  {selectedLog.comments && (
                    <div>
                      <strong>Comments:</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedLog.comments}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Status & Admin Response</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <strong>Status:</strong><br />
                      <span className={`badge ${selectedLog.status === 'Resolved' || selectedLog.status === 'Closed' ? 'badge-success' : selectedLog.status === 'In Progress' ? 'badge-warning' : 'badge-secondary'}`}>
                        {selectedLog.status || 'Pending'}
                      </span>
                    </div>
                    {selectedLog.resolvedAt && (
                      <div>
                        <strong>Resolved At:</strong><br />
                        {new Date(selectedLog.resolvedAt).toLocaleDateString()} at {new Date(selectedLog.resolvedAt).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                  {selectedLog.addressFeedback && (
                    <div>
                      <strong>Address Feedback (Admin):</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedLog.addressFeedback}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Digital Signature</h3>
                  <div style={{ border: '2px solid var(--gray-300)', borderRadius: '0.5rem', padding: '1rem', background: 'white' }}>
                    <img src={selectedLog.digitalSignature} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setResolvingLog(selectedLog);
                setResolveData({ status: selectedLog.status || 'Pending', addressFeedback: selectedLog.addressFeedback || '' });
              }} className="btn btn-primary">
                Resolve / Update Status
              </button>
              <button onClick={() => setSelectedLog(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Visitor Log Modal */}
      {resolvingLog && (
        <div className="modal-overlay" onClick={() => setResolvingLog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resolve Visitor Log</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={resolveData.status}
                  onChange={(e) => setResolveData({ ...resolveData, status: e.target.value })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Acknowledged">Acknowledged</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Address Feedback</label>
                <textarea
                  className="form-textarea"
                  value={resolveData.addressFeedback}
                  onChange={(e) => setResolveData({ ...resolveData, addressFeedback: e.target.value })}
                  placeholder="Add your feedback or response to this visitor log..."
                  maxLength={2000}
                  style={{ minHeight: '120px' }}
                />
                <small style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  {resolveData.addressFeedback.length} / 2000 characters
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleResolveLog} className="btn btn-primary">Save</button>
              <button onClick={() => setResolvingLog(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Resident Feedback Detail Modal */}
      {selectedFeedback && (
        <div className="modal-overlay" onClick={() => setSelectedFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resident Feedback Details</h2>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
                    Resident Information
                    {selectedFeedback.isAnonymous && (
                      <span className="badge badge-secondary" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>Anonymous</span>
                    )}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Resident Name:</strong><br />
                      {selectedFeedback.residentFullName}
                    </div>
                    {selectedFeedback.guardianParentName && (
                      <div>
                        <strong>Guardian/Parent:</strong><br />
                        {selectedFeedback.guardianParentName}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Feedback Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Submitted:</strong><br />
                      {new Date(selectedFeedback.submittedAt).toLocaleDateString()} at {new Date(selectedFeedback.submittedAt).toLocaleTimeString()}
                    </div>
                    <div>
                      <strong>Type:</strong><br />
                      <span className={`badge ${selectedFeedback.feedbackType === 'Complaint' ? 'badge-danger' : selectedFeedback.feedbackType === 'Appreciation' ? 'badge-success' : 'badge-primary'}`}>
                        {selectedFeedback.feedbackType}
                      </span>
                    </div>
                    <div>
                      <strong>Experience Rating:</strong><br />
                      {selectedFeedback.experienceRating}
                    </div>
                    <div>
                      <strong>Service Area:</strong><br />
                      {selectedFeedback.serviceArea}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Detailed Feedback</h3>
                  <div style={{ padding: '1.5rem', background: 'var(--gray-50)', borderRadius: '0.5rem', lineHeight: '1.6' }}>
                    {selectedFeedback.feedbackDetails}
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Digital Signature</h3>
                  <div style={{ border: '2px solid var(--gray-300)', borderRadius: '0.5rem', padding: '1rem', background: 'white' }}>
                    <img src={selectedFeedback.digitalSignature} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Status & Admin Response</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${selectedFeedback.status === 'Resolved' || selectedFeedback.status === 'Closed' ? 'badge-success' : selectedFeedback.status === 'In Progress' ? 'badge-warning' : selectedFeedback.status === 'Acknowledged' ? 'badge-primary' : 'badge-secondary'}`}>
                      {selectedFeedback.status || 'Pending'}
                    </span>
                  </div>
                  {selectedFeedback.adminNotes && (
                    <div>
                      <strong>Admin Notes:</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--secondary)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedFeedback.adminNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setResolvingFeedback(selectedFeedback);
                setResolveFeedbackData({ status: selectedFeedback.status || 'Pending', adminNotes: selectedFeedback.adminNotes || '' });
              }} className="btn btn-primary">
                Resolve / Update Status
              </button>
              <button onClick={() => setSelectedFeedback(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Resident Feedback Modal */}
      {resolvingFeedback && (
        <div className="modal-overlay" onClick={() => setResolvingFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resolve Resident Feedback</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={resolveFeedbackData.status}
                  onChange={(e) => setResolveFeedbackData({ ...resolveFeedbackData, status: e.target.value })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Acknowledged">Acknowledged</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Admin Notes / Remarks</label>
                <textarea
                  className="form-textarea"
                  value={resolveFeedbackData.adminNotes}
                  onChange={(e) => setResolveFeedbackData({ ...resolveFeedbackData, adminNotes: e.target.value })}
                  placeholder="Add your notes or remarks about this feedback..."
                  maxLength={2000}
                  style={{ minHeight: '120px' }}
                />
                <small style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  {resolveFeedbackData.adminNotes.length} / 2000 characters
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleResolveFeedback} className="btn btn-primary">Save</button>
              <button onClick={() => setResolvingFeedback(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Staff Feedback Modal */}
      {resolvingStaffFeedback && (
        <div className="modal-overlay" onClick={() => setResolvingStaffFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resolve Staff Feedback</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={resolveStaffData.status}
                  onChange={(e) => setResolveStaffData({ ...resolveStaffData, status: e.target.value })}
                >
                  <option value="New">New</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Escalated">Escalated</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Admin Notes / Remarks</label>
                <textarea
                  className="form-textarea"
                  value={resolveStaffData.adminNotes}
                  onChange={(e) => setResolveStaffData({ ...resolveStaffData, adminNotes: e.target.value })}
                  placeholder="Add your notes or remarks about this feedback..."
                  maxLength={2000}
                  style={{ minHeight: '120px' }}
                />
                <small style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  {resolveStaffData.adminNotes.length} / 2000 characters
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleResolveStaffFeedback} className="btn btn-primary">Save</button>
              <button onClick={() => setResolvingStaffFeedback(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;